import IconButton from "../../IconButton";

import { useCallback, useEffect, useState } from "react";
import { ReactComponent as Fullscreen } from "../../../assets/ic_fullscreen.svg";
import { ReactComponent as FullscreenActive } from "../../../assets/ic_fullscreen_active.svg";
import { ReactComponent as Mute } from "../../../assets/ic_sound_off.svg";
import { ReactComponent as MuteActive } from "../../../assets/ic_sound_off_active.svg";

import { logE } from "@blacknut/logging/dist";
import { PlayerWebRTC } from "@blacknut/playerwebrtc";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ReactComponent as BackIcon } from "../../../assets/ic_arrow_back.svg";
import { ReactComponent as UnMute } from "../../../assets/ic_sound_on.svg";
import { ReactComponent as UnMuteActive } from "../../../assets/ic_sound_on_active.svg";
import { usePlayerContext } from "../../../utils/PlayerContext";
import { LOGGING_TAG } from "../../../utils/logging";
import styles from "./styles.module.scss";

export interface VideoOverlayProps {
  onExitPressed: () => void;
  videoNode?: HTMLVideoElement;
  className?: string;
  player: PlayerWebRTC;
}

const getFullscreenEnabled = () => {
  return (document as any).webkitFullscreenEnabled || document.fullscreenEnabled;
};

const VideoOverlay = ({
  onExitPressed,
  videoNode,
  className,
  player,
}: VideoOverlayProps) => {
  const { fullscreen, fullscreenHelper } = usePlayerContext();
  const { t } = useTranslation();

  const [muted, setMuted] = useState(videoNode?.muted || false);

  useEffect(() => {
    if (videoNode) {
      const listener = () => {
        setMuted(videoNode?.muted);
      };
      videoNode.addEventListener("volumechange", listener);
      return () => {
        videoNode.removeEventListener("volumechange", listener);
      };
    }
  });

  const toggleMute = useCallback(() => {
    if (videoNode) {
      videoNode.muted = !videoNode.muted;
    }
  }, [videoNode]);
  const setFullscreen = useCallback(async () => {
    try {
      await fullscreenHelper?.requestFullscreen();
    } catch (e) {
      logE(LOGGING_TAG, "Caught error on fullscreen: %o", e);
    }
    player.onEndUserRequestFullscreen();
  }, [player]);
  if (getFullscreenEnabled() && fullscreen) {
    return null;
  }
  return (
    <div className={clsx(styles.container, className)}>
      <IconButton className={styles.back} onClick={onExitPressed}>
        <BackIcon />
      </IconButton>

      <span className={styles.exit}>{t("exitGame.buttons.exit")}</span>
      <div className={styles.buttonBar}>
        {videoNode && muted && (
          <IconButton className={styles.mute} onClick={toggleMute}>
            <Mute />
            <MuteActive />
          </IconButton>
        )}
        {videoNode && !muted && (
          <IconButton className={styles.mute} onClick={toggleMute}>
            <UnMute />
            <UnMuteActive />
          </IconButton>
        )}
        {getFullscreenEnabled() && (
          <IconButton className={styles.fullscreen} onClick={setFullscreen}>
            <Fullscreen />
            <FullscreenActive />
          </IconButton>
        )}
      </div>
    </div>
  );
};
export default VideoOverlay;
